import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import numeral from 'numeral';
import { MonthlySalesChart } from './cards/MonthlySalesChart';
import { InternalExpencesChart } from './cards/InternalExpencesChart';
import { YearSelector } from '../../components/YearSelector';
import { KpiCard } from '../../components/KpiCard';
import { CHART_COLORS } from '../../utils/setChartConfig/chartStyle';
import { setCustomerInvoicesData } from '../../utils/setDataChart/customerInvoicesChart';

const StyledKpis = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(200px, 100%), 1fr));
  gap: 16px;
`;

const StyledTab = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const sumValues = (values) =>
  Object.values(values || {}).reduce((acc, value) => acc + (value || 0), 0);

const formatEuros = (value) => `${numeral(value).format('0,0.00')} €`;

/**
 * ShowFinances component for displaying financial charts and data.
 * @component
 * @param {Object} props - The properties of the ShowFinances component.
 * @param {Object} props.customerInvoices - Object containing customer invoices data.
 * @param {Object} props.internInvoices - Object containing internal invoices data.
 * @param {Function} props.setFinanceDate - Function to set the finance date.
 * @returns {JSX.Element} - JSX element representing the ShowFinances component.
 */
export const ShowFinances = ({
  customerInvoices,
  internInvoices,
  setFinanceDate
}) => {
  const [calculatedCustomerInvoices, setCalculatedCustomerInvoices] = useState(
    {}
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (customerInvoices)
      setCustomerInvoicesData({
        setCalculatedCustomerInvoices,
        customerInvoices
      });
  }, [customerInvoices]);

  const advancePayment = sumValues(calculatedCustomerInvoices.ADVANCE_PAYMENT);
  const balance = sumValues(calculatedCustomerInvoices.BALANCE);
  const internalExpences = (internInvoices?.N || []).reduce(
    (acc, invoice) => acc + (invoice.total_ttc || 0),
    0
  );

  return (
    <StyledTab>
      <YearSelector onChange={setFinanceDate} />
      <StyledKpis>
        <KpiCard
          label={t('dashboard.tabs.finances.total_sales')}
          value={formatEuros(advancePayment + balance)}
          color="#3F3F3F"
        />
        <KpiCard
          label={t('dashboard.tabs.finances.advance_payment')}
          value={formatEuros(advancePayment)}
          color={CHART_COLORS.cambridge}
        />
        <KpiCard
          label={t('dashboard.tabs.finances.balance')}
          value={formatEuros(balance)}
          color={CHART_COLORS.pear}
        />
        <KpiCard
          label={t('dashboard.tabs.finances.internal_expences')}
          value={formatEuros(internalExpences)}
          color={CHART_COLORS.muted}
        />
      </StyledKpis>
      <MonthlySalesChart t={t} customerInvoices={calculatedCustomerInvoices} />
      <InternalExpencesChart t={t} internInvoices={internInvoices} />
    </StyledTab>
  );
};

ShowFinances.propTypes = {
  customerInvoices: PropTypes.shape({}).isRequired,
  internInvoices: PropTypes.shape({
    N: PropTypes.arrayOf(PropTypes.shape({ total_ttc: PropTypes.number }))
  }).isRequired,
  setFinanceDate: PropTypes.func.isRequired
};
